import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { aiCertificationAPI } from '../../services/api';
import { ArrowLeft, Edit, CheckCircle2, Clock, AlertCircle, FileCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ACTIVITIES = [
  {
    key: 'kyc',
    label: 'KYC',
    statuses: ['Complete', 'Pending'],
    remarkOn: 'Pending',
    remarkLabel: 'What data is pending?',
    daysOn: null,
  },
  {
    key: 'cdsl_link',
    label: 'CDSL Link',
    statuses: ['Link sent to Investor', 'Investor has approved the login'],
    remarkOn: null,
    daysOn: 'Link sent to Investor',
    daysLabel: 'Days since link sent',
  },
  {
    key: 'ai_application',
    label: 'AI Application',
    statuses: ['AI Application filed with Agency', 'Application Approved'],
    remarkOn: 'AI Application filed with Agency',
    remarkLabel: 'Remarks (if any)',
    daysOn: 'AI Application filed with Agency',
    daysLabel: 'Days since filed',
  },
  {
    key: 'ai_certificate',
    label: 'AI Certificate',
    statuses: ['Certificate Generated', 'Certificate Received'],
    remarkOn: null,
    daysOn: 'Certificate Generated',
    daysLabel: 'Days since generated',
  },
  {
    key: 'invoicing',
    label: 'Invoicing',
    statuses: ['Invoice raised and sent to Investor', 'Invoice Paid'],
    remarkOn: null,
    daysOn: 'Invoice raised and sent to Investor',
    daysLabel: 'Days since invoice sent',
  },
];

const COMPLETED_STATUSES = {
  kyc: 'Complete',
  cdsl_link: 'Investor has approved the login',
  ai_application: 'Application Approved',
  ai_certificate: 'Certificate Received',
  invoicing: 'Invoice Paid',
};

const STATUS_STYLES = {
  'Complete': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle2 },
  'Pending': { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', icon: AlertCircle },
  'Link sent to Investor': { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: Clock },
  'Investor has approved the login': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle2 },
  'AI Application filed with Agency': { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: Clock },
  'Application Approved': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle2 },
  'Certificate Generated': { bg: 'bg-indigo-50 border-indigo-200', text: 'text-indigo-700', icon: FileCheck },
  'Certificate Received': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle2 },
  'Invoice raised and sent to Investor': { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', icon: Clock },
  'Invoice Paid': { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: CheckCircle2 },
};

const AICertificationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModal, setActiveModal] = useState(null); // { activityKey, ... }
  const [modalStatus, setModalStatus] = useState('');
  const [modalRemarks, setModalRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadRecord();
  }, [id]);

  const loadRecord = async () => {
    try {
      const res = await aiCertificationAPI.getById(id);
      setRecord(res.data);
    } catch (err) {
      alert('Failed to load record');
      navigate('/ai-certification');
    } finally {
      setLoading(false);
    }
  };

  const getNextActivity = () => {
    for (const act of ACTIVITIES) {
      const data = record?.[act.key];
      if (!data || !data.status) return act.key;
      if (data.status !== COMPLETED_STATUSES[act.key]) return act.key;
    }
    return null; // all completed
  };

  const isActivityAccessible = (actKey) => {
    // Users can always skip to any activity
    return true;
  };

  const openActivityModal = (actKey) => {
    if (!canEdit) return;
    const currentData = record?.[actKey];
    setActiveModal(actKey);
    setModalStatus(currentData?.status || '');
    setModalRemarks(currentData?.remarks || '');
  };

  const handleSaveActivity = async () => {
    if (!modalStatus) return;
    setSaving(true);
    try {
      const res = await aiCertificationAPI.updateActivity(id, {
        activity: activeModal,
        status: modalStatus,
        remarks: modalRemarks,
      });
      setRecord(res.data);
      setActiveModal(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;
  if (!record) return null;

  const nextActivity = getNextActivity();

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="ai-certification-detail-page">
      <button onClick={() => navigate('/ai-certification')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4" data-testid="back-btn">
        <ArrowLeft size={16} className="mr-1" /> Back to List
      </button>

      {/* Header */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="investor-name">{record.investor_name}</h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            {record.pan && <span className="font-mono">{record.pan}</span>}
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${record.is_resident ? 'bg-teal-50 text-teal-700' : 'bg-orange-50 text-orange-700'}`}>
              {record.is_resident ? 'Resident' : 'Non-Resident'}
            </span>
            <span className="px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium text-gray-600">{record.investor_type}</span>
          </div>
        </div>
        {canEdit && (
          <button onClick={() => navigate(`/ai-certification/${id}/edit`)} className="inline-flex items-center px-3 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="edit-btn">
            <Edit size={14} className="mr-2" /> Edit Details
          </button>
        )}
      </div>

      {/* Investor Info Card */}
      <div className="bg-white border rounded-lg p-5 mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wider">Investor Details</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div><span className="text-gray-500 block text-xs">Address</span><span className="text-gray-900">{record.address || '--'}</span></div>
          <div><span className="text-gray-500 block text-xs">City</span><span className="text-gray-900">{record.city || '--'}</span></div>
          <div><span className="text-gray-500 block text-xs">State</span><span className="text-gray-900">{record.state || '--'}</span></div>
          <div><span className="text-gray-500 block text-xs">Pin/Zip</span><span className="text-gray-900">{record.pin_zip || '--'}</span></div>
          <div><span className="text-gray-500 block text-xs">Email</span><span className="text-gray-900">{record.email || '--'}</span></div>
          <div><span className="text-gray-500 block text-xs">Mobile</span><span className="text-gray-900">{record.mobile || '--'}</span></div>
        </div>
      </div>

      {/* Workflow Progress */}
      <div className="mb-6">
        <h2 className="text-sm font-semibold text-gray-800 mb-3 uppercase tracking-wider">Certification Workflow</h2>
        
        {/* Progress Bar */}
        <div className="flex items-center gap-1 mb-6">
          {ACTIVITIES.map((act, idx) => {
            const data = record[act.key];
            const isComplete = data?.status === COMPLETED_STATUSES[act.key];
            const isActive = data?.status && !isComplete;
            return (
              <React.Fragment key={act.key}>
                <div className={`flex-1 h-2 rounded-full ${isComplete ? 'bg-green-500' : isActive ? 'bg-blue-400' : 'bg-gray-200'}`} />
                {idx < ACTIVITIES.length - 1 && <div className="w-1" />}
              </React.Fragment>
            );
          })}
        </div>

        {/* Activity Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ACTIVITIES.map((act, idx) => {
            const data = record[act.key];
            const currentStatus = data?.status;
            const isComplete = currentStatus === COMPLETED_STATUSES[act.key];
            const isNext = act.key === nextActivity;
            const style = currentStatus ? STATUS_STYLES[currentStatus] : null;
            const IconComp = style?.icon || Clock;

            return (
              <div
                key={act.key}
                className={`border rounded-lg p-4 transition-all ${
                  isNext && !currentStatus ? 'border-blue-300 bg-blue-50/30 ring-1 ring-blue-200' :
                  currentStatus ? `${style?.bg} border` : 'bg-white'
                } ${canEdit ? 'cursor-pointer hover:shadow-md' : ''}`}
                onClick={() => canEdit && openActivityModal(act.key)}
                data-testid={`activity-card-${act.key}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-400">STEP {idx + 1}</span>
                    <h3 className="font-semibold text-gray-900 text-sm">{act.label}</h3>
                  </div>
                  {currentStatus && <IconComp size={18} className={style?.text} />}
                </div>

                {currentStatus ? (
                  <div>
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${style?.text} ${style?.bg}`}>
                      {currentStatus}
                    </span>
                    {data.days_since !== undefined && data.days_since !== null && (
                      <span className="ml-2 text-xs text-gray-500">({data.days_since} days ago)</span>
                    )}
                    {data.remarks && (
                      <p className="mt-2 text-xs text-gray-600 bg-white/60 rounded px-2 py-1">
                        <span className="font-medium">Remarks:</span> {data.remarks}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">
                    {isNext ? 'Click to set status (next in workflow)' : 'Click to set status'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Activity Status Modal */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setActiveModal(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-6" onClick={e => e.stopPropagation()} data-testid="activity-modal">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Update {ACTIVITIES.find(a => a.key === activeModal)?.label} Status
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="space-y-2">
                  {ACTIVITIES.find(a => a.key === activeModal)?.statuses.map(s => (
                    <label key={s} className={`flex items-center gap-3 px-3 py-2.5 border rounded-lg cursor-pointer transition-all ${
                      modalStatus === s ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-200' : 'hover:bg-gray-50'
                    }`}>
                      <input type="radio" name="activity-status" value={s} checked={modalStatus === s}
                        onChange={() => setModalStatus(s)} className="text-blue-600" data-testid={`status-radio-${s.replace(/\s+/g, '-').toLowerCase()}`} />
                      <span className="text-sm text-gray-800">{s}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Show remarks input based on activity config */}
              {(() => {
                const actConfig = ACTIVITIES.find(a => a.key === activeModal);
                const showRemarks = (actConfig?.remarkOn && modalStatus === actConfig.remarkOn) ||
                  (activeModal === 'ai_application' && modalStatus === 'AI Application filed with Agency');
                if (!showRemarks) return null;
                return (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {actConfig?.remarkLabel || 'Remarks'}
                    </label>
                    <textarea value={modalRemarks} onChange={e => setModalRemarks(e.target.value)}
                      rows={3} className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                      placeholder="Enter details..." data-testid="remarks-input" />
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setActiveModal(null)} className="px-4 py-2 border rounded-lg text-sm text-gray-700 hover:bg-gray-50" data-testid="modal-cancel-btn">
                Cancel
              </button>
              <button onClick={handleSaveActivity} disabled={!modalStatus || saving}
                className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50" data-testid="modal-save-btn">
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AICertificationDetail;
